<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import ShipmentGeneralFieldsCard from '@/components/shipment/form/general/ShipmentGeneralFieldsCard.vue';
import ShipmentFormStopsSection from '@/components/shipment/form/stops/ShipmentFormStopsSection.vue';
import ShipmentTrackerPickerSection from '@/components/shipment/form/tracker/ShipmentTrackerPickerSection.vue';
import ShipmentTrackerLockedCard from '@/components/shipment/form/tracker/ShipmentTrackerLockedCard.vue';
import { useShipmentFormStore } from '@/stores/useShipmentFormStore';
import type { ShipmentFormGeneralErrors } from '@/composables/useShipmentFormValidation';
import type { ShipmentFormPageContext } from '@/components/shipment/form/types/shipment-form-page-context';
import { useShipmentFormStopsSession } from '@/composables/useShipmentFormStopsSession';
import type { TrackerPickerValue } from '@gpx-spa/ui';
import type { ShipmentFormStopsPort } from '@/composables/shipment-form-stops-port';

export type ShipmentFormSection = 'general' | 'stops' | 'tracker' | 'all';

type FocusableSectionRef = {
  focusFirstField?: () => boolean;
};

const props = withDefaults(
  defineProps<{
    visibleSection?: ShipmentFormSection;
    /** @deprecated use visibleSection */
    section?: ShipmentFormSection;
    generalErrors: ShipmentFormGeneralErrors;
    showPlannedRange?: boolean;
    showStops?: boolean;
    metadataReadOnly?: boolean;
    pageContext?: ShipmentFormPageContext;
    trackerMode?: 'picker' | 'locked' | 'hidden';
    deviceError?: string;
    loadDevicesWhenVisible?: boolean;
    assignedDevices?:
      | {
          id: number;
          name: string;
          serial: string | null;
          imei: string | null;
        }[]
      | null;
    lockLabel?: string;
  }>(),
  {
    visibleSection: undefined,
    section: 'all',
    showPlannedRange: true,
    showStops: true,
    metadataReadOnly: false,
    trackerMode: 'hidden',
    deviceError: '',
    loadDevicesWhenVisible: true,
    assignedDevices: null,
    lockLabel: 'Assigned at creation',
  },
);

const devicesValue = defineModel<TrackerPickerValue[]>('devices', {
  default: () => [],
});

const activeSection = computed(
  () => props.visibleSection ?? props.section ?? 'all',
);

const formStore = useShipmentFormStore();
const {
  generalName,
  generalCarrier,
  generalNotes,
  generalAssetValueCents,
  generalPlannedRange,
} = storeToRefs(formStore);

const stopsSession = useShipmentFormStopsSession();
const stopsSectionRef = ref<InstanceType<
  typeof ShipmentFormStopsSection
> | null>(null);
const generalFieldsRef = ref<FocusableSectionRef | null>(null);
const trackerPickerSectionRef = ref<FocusableSectionRef | null>(null);

function bindStopsSectionRef(el: unknown) {
  const port = el as ShipmentFormStopsPort | null;
  stopsSectionRef.value = el as InstanceType<
    typeof ShipmentFormStopsSection
  > | null;
  stopsSession?.registerStopsPort(port);
}

const showGeneral = computed(
  () => activeSection.value === 'general' || activeSection.value === 'all',
);
const showStopsSection = computed(
  () =>
    props.showStops &&
    (activeSection.value === 'stops' || activeSection.value === 'all'),
);
const showTrackerPicker = computed(
  () =>
    props.trackerMode === 'picker' &&
    (activeSection.value === 'tracker' || activeSection.value === 'all'),
);
const showTrackerLocked = computed(
  () =>
    props.trackerMode === 'locked' &&
    (activeSection.value === 'tracker' || activeSection.value === 'all'),
);

function focusSection(name: 'tracker' | 'general' | 'stops'): boolean {
  if (name === 'tracker') {
    return trackerPickerSectionRef.value?.focusFirstField?.() ?? false;
  }
  if (name === 'general') {
    return generalFieldsRef.value?.focusFirstField?.() ?? false;
  }
  return stopsSectionRef.value?.focusFirstField?.() ?? false;
}

function focusActiveSection(): boolean {
  const section = activeSection.value;
  if (section === 'tracker') return focusSection('tracker');
  if (section === 'general') return focusSection('general');
  if (section === 'stops') return focusSection('stops');
  if (showTrackerPicker.value && focusSection('tracker')) return true;
  if (showGeneral.value && focusSection('general')) return true;
  if (showStopsSection.value && focusSection('stops')) return true;
  return false;
}

defineExpose({
  stopsSectionRef,
  focusActiveSection,
  focusSection,
});
</script>

<template>
  <div class="space-y-6">
    <slot name="header" />

    <ShipmentTrackerLockedCard
      v-if="showTrackerLocked"
      :devices="assignedDevices"
      :lock-label="lockLabel"
    />
    <ShipmentTrackerPickerSection
      v-else-if="showTrackerPicker"
      ref="trackerPickerSectionRef"
      v-model:devices="devicesValue"
      :device-error="deviceError"
      :load-devices-when-visible="loadDevicesWhenVisible"
    />

    <ShipmentGeneralFieldsCard
      v-if="showGeneral"
      ref="generalFieldsRef"
      v-model:name="generalName"
      v-model:carrier="generalCarrier"
      v-model:notes="generalNotes"
      v-model:asset-value-cents="generalAssetValueCents"
      v-model:planned-range="generalPlannedRange"
      :errors="generalErrors"
      :show-planned-range="showPlannedRange"
      :name-read-only="metadataReadOnly"
      :asset-value-read-only="metadataReadOnly"
    />

    <ShipmentFormStopsSection
      v-if="showStopsSection"
      :ref="bindStopsSectionRef"
      :page-context="pageContext"
    />

    <slot name="footer" />
  </div>
</template>
